import { Formik } from 'formik';
import { useState, FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiOutlineArrowLeft, HiOutlineMap, HiOutlineX, HiOutlineCheck } from 'react-icons/hi';
import { useSearchQuery, useTour } from '../../../../../hooks';
import { tourSchema } from '../../../../../constants';
import TourForm from '../sections/TourForm';
import DashboardLoader from '../../../sections/Loader/DashboardLoader';
import { Alert, CustomEditor, ImageUploader, SEO } from '../../../../commons';
import { DAV_APIS } from '../../../../../adapters';
import SwitchInput from '../../../../commons/SwitchInput';
import { Card } from '../../../../ui/Card';
import { Button } from '../../../../ui/Button';

const EditTourPage: FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const tourId = useSearchQuery('tour');
  const { tour, loading } = useTour(tourId as string);

  const editTourSubmitHandler = async (values: any) => {
    setIsLoading(true);
    try {
      const tourActivities: string[] = [];
      values.tourActivities.split('\n').map((item: string) => tourActivities.push(item));

      const priceIncludes: string[] = [];
      values.priceIncludes.split('\n').map((item: string) => priceIncludes.push(item));

      const priceExcludes: string[] = [];
      values.priceExcludes.split('\n').map((item: string) => priceExcludes.push(item));

      const packageDetails = {
        price_inludes: priceIncludes,
        price_excludes: priceExcludes,
      };

      if (values.map instanceof File) {
        const mapUrl = await DAV_APIS.tour.uploadImage(values.map);
        values.map = mapUrl?.data?.url;
      }

      const data = {
        name: values.title,
        description: values.description,
        tourActivities: JSON.stringify(tourActivities),
        file: values.imageFile,
        country: values.country.toLowerCase(),
        category: values.category,
        duration: values.days,
        price: values.price,
        dayActivityDescription: JSON.stringify(values.itineraries),
        packageDetails: JSON.stringify(packageDetails),
        key_words: JSON.stringify(values.key_words),
        journey: values.journey,
        isFeatured: values.isFeatured,
        map: values.map,
        schedules: values.enableScheduledTours ? values.schedules : [],
      };
      await DAV_APIS.tour.editTour(data, tourId as string);
      setIsLoading(false);
      Alert({ variant: 'success', message: 'Tour package edited successfully' });
      navigate('/dashboard/tours');
    } catch (error) {
      setIsLoading(false);
      Alert({ variant: 'error', message: 'Failed to edit package, Please try again' });
    }
  };

  let tourActivities: string = '';
  let priceIncludes: string = '';
  let priceExcludes: string = '';

  /* eslint-disable no-return-assign */
  tour?.tourActivities?.map((el) => el.length > 0 && (tourActivities += `${el}\n`));
  tour?.packageDetails?.price_inludes?.map((el) => el.length > 0 && (priceIncludes += `${el}\n`));
  tour?.packageDetails?.price_excludes?.map((el) => el.length > 0 && (priceExcludes += `${el}\n`));
  /* eslint-enable no-return-assign */

  return (
    <div className='w-full max-w-7xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700'>
      {/* Header */}
      <div className='flex items-center gap-4'>
        <Button
          variant='ghost'
          onClick={() => navigate('/dashboard/tours')}
          leftIcon={<HiOutlineArrowLeft className='w-5 h-5' />}
        >
          Back to Tours
        </Button>
      </div>

      {loading ? (
        <div className='w-full min-h-[400px] flex justify-center items-center'>
          <DashboardLoader />
        </div>
      ) : (
        <>
          <Formik
            enableReinitialize
            initialValues={{
              title: tour?.name || '',
              description: tour?.description || '',
              imageCover: tour?.imageCover || '',
              imageFile: '',
              tourActivities: tourActivities || '',
              category: tour?.category || '',
              country: tour?.country || '',
              days: tour?.duration || '',
              price: tour?.price || '',
              priceIncludes: priceIncludes || '',
              priceExcludes: priceExcludes || '',
              itineraries: tour?.dayActivityDescription || [],
              day: '',
              itinaryTitle: '',
              itinaryDescription: '',
              meal_plan: '',
              accommodation_plan: '',
              key_words: tour?.key_words || [],
              journey: tour?.journey || '',
              isFeatured: tour?.isFeatured || false,
              map: tour?.map || '',
              schedules: (tour as any)?.schedules || [],
              enableScheduledTours: (tour as any)?.schedules?.length > 0 || false,
            }}
            validationSchema={tourSchema}
            onSubmit={async (values, { resetForm }) => {
              await editTourSubmitHandler(values);
              resetForm();
            }}
          >
            {({ handleSubmit, values, setFieldValue, dirty, isValid }) => (
              <form onSubmit={handleSubmit}>
                <Card variant='glass' className='p-6 md:p-8 border-none shadow-xl shadow-gray-200/50 dark:shadow-none'>
                  <div className='mb-8 border-b border-gray-100 dark:border-gray-700/50 pb-6'>
                    <h2 className='text-2xl font-bold text-gray-900 dark:text-white'>Edit Tour Details</h2>
                    <p className='text-gray-500 dark:text-gray-400 mt-1'>Update the comprehensive details for this tour package.</p>
                  </div>

                  <div className='space-y-8'>
                    <TourForm values={values} setFieldValue={setFieldValue} edit />
                    
                    <div className='pt-4 border-t border-gray-100 dark:border-gray-700/50'>
                      <section>
                        <div className='flex items-center gap-2 mb-6 text-primary-600 dark:text-primary-400'>
                          <HiOutlineMap className='w-5 h-5' />
                          <h3 className='text-lg font-semibold text-gray-800 dark:text-gray-100'>
                            Additional Options & Journey Map
                            <span className='ml-2 text-sm font-normal text-gray-500 dark:text-gray-400'>(Optional)</span>
                          </h3>
                        </div>
                        
                        <div className='space-y-8 px-2'>
                          <div className='space-y-4'>
                            <div className='flex items-center gap-2 text-primary-600 dark:text-primary-400'>
                                <h4 className='text-sm uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400'>Journey Details</h4>
                            </div>
                            <CustomEditor
                              placeholder='Type a summarized tour journey from start to finish here...'
                              onChange={(val: any) => setFieldValue('journey', val)}
                              value={values.journey}
                              name='journey'
                            />
                          </div>
                          
                          <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
                            <div className='p-5 bg-gray-50 dark:bg-gray-800/50 rounded-2xl border border-gray-100 dark:border-gray-700/50 hover:border-primary-200 transition-colors'>
                              <h5 className='text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center gap-2'>
                                <span className='w-1.5 h-1.5 rounded-full bg-primary-500' />
                                Journey Map
                              </h5>
                              <ImageUploader label='Upload/Change Map' initialUrl={typeof values.map === 'string' ? values.map : undefined} onUpload={(file) => setFieldValue('map', file)} />
                            </div>
                            <div className='p-5 bg-gray-50 dark:bg-gray-800/50 rounded-2xl border border-gray-100 dark:border-gray-700/50 hover:border-primary-200 transition-colors flex flex-col justify-center'>
                                <h5 className='text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center gap-2'>
                                <span className='w-1.5 h-1.5 rounded-full bg-yellow-400' />
                                Featured Status
                              </h5>
                              <div className='flex items-center justify-between bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700'>
                                <span className='text-sm text-gray-600 dark:text-gray-300'>Show on Landing Page</span>
                                <SwitchInput
                                  checked={values.isFeatured}
                                  onChange={(event) => setFieldValue('isFeatured', event)}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </section>
                    </div>

                    <div className='flex justify-end gap-3 pt-6 border-t border-gray-100 dark:border-gray-700/50'>
                      <Button
                        variant='outline-secondary'
                        onClick={() => navigate('/dashboard/tours')}
                        leftIcon={<HiOutlineX className='w-4 h-4' />}
                      >
                        Cancel
                      </Button>
                      <Button
                        type='submit'
                        variant='primary'
                        loading={isLoading}
                        disabled={isLoading || !dirty || !isValid}
                        className='min-w-[140px]'
                        leftIcon={!isLoading ? <HiOutlineCheck className='w-4 h-4' /> : undefined}
                      >
                        {isLoading ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </div>
                  </div>
                </Card>
              </form>
            )}
          </Formik>

          <SEO
            isEdit
            pageType='tour'
            initialData={{
              title: tour.name,
              description: tour.description,
              image: tour.imageCover,
              key_words: tour.key_words,
              slug: tour.slug,
            }}
          />
        </>
      )}
    </div>
  );
};

export default EditTourPage;
